import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Select, Tag, Typography, message } from 'antd';
import * as THREE from 'three';
import * as OBC from '@thatopen/components';
import * as OBCF from '@thatopen/components-front';
import * as OBF from '@thatopen/fragments';
import * as WEBIFC from 'web-ifc';
import styles from './IFCViewer.module.less';
import Loading from '../../../../components/Loading';
import TuzhiVisService from '../../TuzhiVis.service';
import { useResizeDetector } from 'react-resize-detector';
import {
  IFC_PROFILE_DESCRIPTIONS,
  canUseIfcVersionFile,
  getIfcDurationDisplay,
  getIfcVersionByProfile,
  IFC_PROFILE_LABELS,
  IFC_STATUS_LABELS,
  IFC_STATUS_TAG_COLORS,
  IfcProfile,
  IfcVersionItem,
} from './ifcVersions';

interface IFCViewerProps {
  spaceId: number;
  ifcVersions: IfcVersionItem[];
  selectedProfile: IfcProfile | null;
  versionsLoading: boolean;
  actionProfile: IfcProfile | null;
  onSelectProfile: (profile: IfcProfile) => void;
  onGenerateProfile: (profile: IfcProfile, options?: { regenerate?: boolean }) => void;
  onDownloadProfile: (profile: IfcProfile) => void;
}

interface StoreyOption {
  id: number;
  key: string;
  name: string;
}

interface EntityToggleConfig {
  key: string;
  label: string;
  entities: string[];
}

interface DoorEvacuationBinding {
  hasEvacuationPath: boolean;
  doorEI: string | null;
  primaryEvacuationPathGlobalId: string | null;
  primaryEvacuationPathStepId: string | null;
  primaryEvacuationPathStrategy: string | null;
}

interface EvacuationPathBinding {
  pathGlobalId: string | null;
  pathStepId: string | null;
  strategy: string | null;
  doorEI: string | null;
  doorGlobalId: string | null;
  doorStepId: string | null;
  relatedLightGlobalIds: string[];
  relatedLightStepIds: string[];
}

type IfcEntityProperties = {
  expressID?: number;
  type?: number;
  [key: string]: any;
};

const DOOR_BINDING_PSET = 'Pset_TuzhiEvacuationDoorBinding';
const EVACUATION_PATH_PSET = 'Pset_TuzhiEvacuationPath';
const EVACUATION_LIGHT_PSET = 'Pset_TuzhiEvacuationLightBinding';

const ENTITY_TOGGLE_CONFIGS: EntityToggleConfig[] = [
  { key: 'spaces', label: '房间', entities: ['IFCSPACE'] },
  { key: 'walls', label: '墙', entities: ['IFCWALLSTANDARDCASE', 'IFCWALL'] },
  { key: 'doors', label: '门', entities: ['IFCDOOR'] },
  { key: 'windows', label: '窗', entities: ['IFCWINDOW'] },
  { key: 'columns', label: '柱', entities: ['IFCCOLUMN'] },
  { key: 'roofs', label: '屋顶', entities: ['IFCROOF'] },
  { key: 'lightFixtures', label: 'IfcLightFixture', entities: ['IFCLIGHTFIXTURE'] },
  { key: 'buildingElementProxy', label: 'IfcBuildingElementProxy', entities: ['IFCBUILDINGELEMENTPROXY'] },
  { key: 'switchingDevice', label: 'IfcSwitchingDevice', entities: ['IFCSWITCHINGDEVICE'] },
  { key: 'sensor', label: 'IfcSensor', entities: ['IFCSENSOR'] },
  { key: 'systemFurnitureElement', label: 'IfcSystemFurnitureElement', entities: ['IFCSYSTEMFURNITUREELEMENT'] },
];

const DEFAULT_ENTITY_VISIBILITY = ENTITY_TOGGLE_CONFIGS.reduce<Record<string, boolean>>((acc, config) => {
  acc[config.key] = config.key !== 'spaces';
  return acc;
}, {});

function IFCViewer(props: IFCViewerProps) {
  const {
    spaceId,
    ifcVersions,
    selectedProfile,
    versionsLoading,
    actionProfile,
    onSelectProfile,
    onGenerateProfile,
    onDownloadProfile,
  } = props;
  const ifcRef = useRef<HTMLDivElement>(null);
  const hiderRef = useRef<OBC.Hider | null>(null);
  const indexerRef = useRef<OBC.IfcRelationsIndexer | null>(null);
  const highlighterRef = useRef<OBCF.Highlighter | null>(null);
  const propertySetCacheRef = useRef<Map<string, Record<string, unknown> | null>>(new Map());
  const evacuationPathIdsRef = useRef<Set<number>>(new Set());
  const evacuationPathFragmentMapRef = useRef<OBF.FragmentIdMap | null>(null);
  const evacuationPathByGlobalIdRef = useRef<Map<string, Set<number>>>(new Map());
  const evacuationPathByStepIdRef = useRef<Map<string, Set<number>>>(new Map());
  const evacuationLightByGlobalIdRef = useRef<Map<string, number>>(new Map());
  const evacuationLightByStepIdRef = useRef<Map<string, number>>(new Map());
  const currentEvacuationPathMapRef = useRef<OBF.FragmentIdMap | null>(null);
  const currentEvacuationLightMapRef = useRef<OBF.FragmentIdMap | null>(null);

  const [world, setWorld] = useState<any>(null);
  const [model, setModel] = useState<any>(null);
  const [classifier, setClassifier] = useState<OBC.Classifier | null>(null);
  const [hider, setHider] = useState<OBC.Hider | null>(null);
  const [indexer, setIndexer] = useState<OBC.IfcRelationsIndexer | null>(null);
  const [bboxer, setBboxer] = useState<OBC.BoundingBoxer | null>(null);
  const [storeys, setStoreys] = useState<StoreyOption[]>([]);
  const [selectedStoreyKey, setSelectedStoreyKey] = useState('all');
  const [entityVisibility, setEntityVisibility] = useState<Record<string, boolean>>(DEFAULT_ENTITY_VISIBILITY);
  const [loaded, setLoaded] = useState(false);
  const [hasIfcFile, setHasIfcFile] = useState(true);
  const [emptyStateMessage, setEmptyStateMessage] = useState('暂无可预览的IFC模型');
  const [expandedProfile, setExpandedProfile] = useState<IfcProfile | null>(null);

  const { width, height, ref: resizeRef } = useResizeDetector({
  });

  const storeySelectOptions = useMemo(
    () => [
      { label: '全部楼层', value: 'all' },
      ...storeys.map((storey) => ({ label: storey.name, value: storey.key })),
    ],
    [storeys],
  );

  const activeVersion = useMemo(
    () => getIfcVersionByProfile(ifcVersions, selectedProfile),
    [ifcVersions, selectedProfile],
  );
  const activeVersionProfile = activeVersion?.profile ?? null;
  const activeVersionStatus = activeVersion?.status ?? null;
  const activeVersionExists = activeVersion?.exists ?? false;
  const activeVersionDownloadUrl = activeVersion?.downloadUrl ?? null;
  const activeVersionCanUseFile = Boolean(activeVersionProfile && activeVersionStatus === 'ready' && activeVersionExists);
  const activeVersionEmptyMessage = useMemo(() => {
    if (!activeVersionProfile) {
      return '暂无 IFC 版本信息';
    }
    switch (activeVersionStatus) {
      case 'missing':
        return '当前版本尚未生成';
      case 'generating':
        return '当前版本正在生成中';
      case 'failed':
        return '当前版本生成失败，请重试';
      default:
        return '当前版本暂无可预览模型';
    }
  }, [activeVersionProfile, activeVersionStatus]);

  useEffect(() => {
    if (!ifcVersions.length) {
      setExpandedProfile(null);
      return;
    }

    setExpandedProfile((prev) => {
      if (selectedProfile) {
        return selectedProfile;
      }
      if (prev && ifcVersions.some((version) => version.profile === prev)) {
        return prev;
      }
      return ifcVersions.find((version) => canUseIfcVersionFile(version))?.profile
        ?? ifcVersions.find((version) => version.isDefault)?.profile
        ?? ifcVersions[0].profile;
    });
  }, [ifcVersions, selectedProfile]);

  const getPrimitiveValue = useCallback((value: unknown): string | number | boolean | null => {
    if (value == null) {
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'object' && 'value' in value) {
      return getPrimitiveValue((value as { value?: unknown }).value);
    }
    return null;
  }, []);

  const getStringValue = useCallback((value: unknown): string | null => {
    const primitive = getPrimitiveValue(value);
    if (primitive == null) {
      return null;
    }
    const normalized = String(primitive).trim();
    return normalized ? normalized : null;
  }, [getPrimitiveValue]);

  const getBooleanValue = useCallback((value: unknown): boolean => {
    const primitive = getPrimitiveValue(value);
    if (typeof primitive === 'boolean') {
      return primitive;
    }
    if (typeof primitive === 'number') {
      return primitive !== 0;
    }
    if (typeof primitive === 'string') {
      const normalized = primitive.trim().toLowerCase();
      return ['true', '1', 'yes', 'y', 't'].includes(normalized);
    }
    return false;
  }, [getPrimitiveValue]);

  const getStringListValue = useCallback((value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => getStringValue(item))
        .filter((item): item is string => Boolean(item));
    }

    const normalized = getStringValue(value);
    if (!normalized) {
      return [];
    }

    return normalized
      .split(/[,\n;|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }, [getStringValue]);

  const getIfcPropertyValue = useCallback((property: IfcEntityProperties | null): unknown => {
    if (!property) {
      return null;
    }
    if (property.NominalValue !== undefined) {
      return getPrimitiveValue(property.NominalValue);
    }
    if (Array.isArray(property.ListValues)) {
      return property.ListValues.map((item) => getPrimitiveValue(item)).filter((item) => item != null);
    }
    if (Array.isArray(property.EnumerationValues)) {
      return property.EnumerationValues.map((item) => getPrimitiveValue(item)).filter((item) => item != null);
    }
    return null;
  }, [getPrimitiveValue]);

  const getStoreyFragmentMap = useCallback((storey: StoreyOption) => {
    if (!indexer || !model) {
      return null;
    }
    const storeyChildren = indexer.getEntityChildren(model, storey.id);
    return model.getFragmentMap(storeyChildren);
  }, [indexer, model]);

  const syncEvacuationPathVisibility = useCallback(() => {
    const currentHider = hiderRef.current;
    if (!currentHider) {
      return;
    }

    const allPathMap = evacuationPathFragmentMapRef.current;
    if (allPathMap) {
      currentHider.set(false, allPathMap);
    }

    if (currentEvacuationPathMapRef.current) {
      currentHider.set(true, currentEvacuationPathMapRef.current);
    }
  }, []);

  const clearEvacuationHighlight = useCallback(() => {
    const highlighter = highlighterRef.current;
    if (highlighter) {
      highlighter.clear('evacuationPath');
      highlighter.clear('evacuationLights');
    }
    currentEvacuationPathMapRef.current = null;
    currentEvacuationLightMapRef.current = null;
    syncEvacuationPathVisibility();
  }, [syncEvacuationPathVisibility]);

  const getPropertyValuesFromPsetId = useCallback(async (modelGroup: OBF.FragmentsGroup, psetId: number) => {
    const propertyIds = await OBC.IfcPropertiesUtils.getPsetProps(modelGroup, psetId);
    const values: Record<string, unknown> = {};
    for (const propertyId of propertyIds ?? []) {
      const property = await modelGroup.getProperties(propertyId) as IfcEntityProperties | null;
      const propertyName = getStringValue(property?.Name);
      if (!propertyName) {
        continue;
      }
      values[propertyName] = getIfcPropertyValue(property);
    }
    return values;
  }, [getIfcPropertyValue, getStringValue]);

  const getPropertySetValues = useCallback(async (modelGroup: OBF.FragmentsGroup, expressID: number, psetName: string) => {
    const cacheKey = `${expressID}:${psetName}`;
    if (propertySetCacheRef.current.has(cacheKey)) {
      return propertySetCacheRef.current.get(cacheKey) ?? null;
    }

    const relationsIndexer = indexerRef.current;
    if (!relationsIndexer) {
      return null;
    }

    const definitionIds = relationsIndexer.getEntityRelations(modelGroup, expressID, 'IsDefinedBy') ?? [];
    for (const definitionId of definitionIds) {
      const pset = await modelGroup.getProperties(definitionId) as IfcEntityProperties | null;
      const currentPsetName = getStringValue(pset?.Name);
      if (currentPsetName !== psetName) {
        continue;
      }

      const values = await getPropertyValuesFromPsetId(modelGroup, definitionId);
      propertySetCacheRef.current.set(cacheKey, values);
      return values;
    }

    propertySetCacheRef.current.set(cacheKey, null);
    return null;
  }, [getPropertyValuesFromPsetId, getStringValue]);

  const getDoorEvacuationBinding = useCallback(async (modelGroup: OBF.FragmentsGroup, expressID: number): Promise<DoorEvacuationBinding | null> => {
    const values = await getPropertySetValues(modelGroup, expressID, DOOR_BINDING_PSET);
    if (!values) {
      return null;
    }

    return {
      hasEvacuationPath: getBooleanValue(values.HasEvacuationPath),
      doorEI: getStringValue(values.DoorEI),
      primaryEvacuationPathGlobalId: getStringValue(values.PrimaryEvacuationPathGlobalId),
      primaryEvacuationPathStepId: getStringValue(values.PrimaryEvacuationPathStepId),
      primaryEvacuationPathStrategy: getStringValue(values.PrimaryEvacuationPathStrategy),
    };
  }, [getBooleanValue, getPropertySetValues, getStringValue]);

  const getEvacuationPathBinding = useCallback(async (modelGroup: OBF.FragmentsGroup, expressID: number): Promise<EvacuationPathBinding | null> => {
    const values = await getPropertySetValues(modelGroup, expressID, EVACUATION_PATH_PSET);
    if (!values) {
      return null;
    }

    return {
      pathGlobalId: getStringValue(values.PathGlobalId),
      pathStepId: getStringValue(values.PathStepId),
      strategy: getStringValue(values.Strategy),
      doorEI: getStringValue(values.DoorEI),
      doorGlobalId: getStringValue(values.DoorGlobalId),
      doorStepId: getStringValue(values.DoorStepId),
      relatedLightGlobalIds: getStringListValue(values.RelatedLightGlobalIds),
      relatedLightStepIds: getStringListValue(values.RelatedLightStepIds),
    };
  }, [getPropertySetValues, getStringListValue, getStringValue]);

  const findEntityByGlobalId = useCallback(async (modelGroup: OBF.FragmentsGroup, globalId: string, preferredIndex?: Map<string, number>) => {
    const normalizedGlobalId = getStringValue(globalId);
    if (!normalizedGlobalId) {
      return null;
    }
    if (preferredIndex?.has(normalizedGlobalId)) {
      return preferredIndex.get(normalizedGlobalId) ?? null;
    }

    const entity = await OBC.IfcPropertiesUtils.findItemByGuid(modelGroup, normalizedGlobalId);
    return typeof entity?.expressID === 'number' ? entity.expressID : null;
  }, [getStringValue]);

  const findEntityByStepId = useCallback(async (modelGroup: OBF.FragmentsGroup, stepId: string, preferredIndex?: Map<string, number>) => {
    const normalizedStepId = getStringValue(stepId);
    if (!normalizedStepId) {
      return null;
    }
    if (preferredIndex?.has(normalizedStepId)) {
      return preferredIndex.get(normalizedStepId) ?? null;
    }

    const numericId = Number(normalizedStepId);
    if (!Number.isFinite(numericId)) {
      return null;
    }

    const entity = await modelGroup.getProperties(numericId);
    return entity ? numericId : null;
  }, [getStringValue]);

  const findEntitiesByGlobalIds = useCallback(async (modelGroup: OBF.FragmentsGroup, globalIds: string[]) => {
    const found = new Set<number>();
    for (const globalId of globalIds) {
      const entityId = await findEntityByGlobalId(modelGroup, globalId, evacuationLightByGlobalIdRef.current);
      if (entityId != null) {
        found.add(entityId);
      }
    }
    return Array.from(found);
  }, [findEntityByGlobalId]);

  const findPathEntitiesByGlobalId = useCallback((globalId: string) => {
    const normalizedGlobalId = getStringValue(globalId);
    if (!normalizedGlobalId) {
      return [];
    }
    return Array.from(evacuationPathByGlobalIdRef.current.get(normalizedGlobalId) ?? []);
  }, [getStringValue]);

  const findPathEntitiesByStepId = useCallback((stepId: string) => {
    const normalizedStepId = getStringValue(stepId);
    if (!normalizedStepId) {
      return [];
    }
    return Array.from(evacuationPathByStepIdRef.current.get(normalizedStepId) ?? []);
  }, [getStringValue]);

  const getExpressIdFromSelection = useCallback((selection: OBF.FragmentIdMap) => {
    const fragmentIds = Object.keys(selection);
    if (!fragmentIds.length) {
      return null;
    }
    const expressIds = Array.from(selection[fragmentIds[0]]);
    return expressIds.length ? expressIds[0] : null;
  }, []);

  const getEntityWithChildrenFragmentMap = useCallback((modelGroup: OBF.FragmentsGroup, expressID: number) => {
    const relationsIndexer = indexerRef.current;
    if (!relationsIndexer) {
      return modelGroup.getFragmentMap([expressID]);
    }

    const ids = new Set<number>([expressID]);
    const children = relationsIndexer.getEntityChildren(modelGroup, expressID);
    for (const childId of Array.from(children)) {
      ids.add(childId);
    }
    return modelGroup.getFragmentMap(ids);
  }, []);

  const collectEntityAndDescendantIds = useCallback((modelGroup: OBF.FragmentsGroup, expressID: number) => {
    const collected = new Set<number>([expressID]);
    const relationsIndexer = indexerRef.current;
    if (!relationsIndexer) {
      return collected;
    }

    const queue = [expressID];
    while (queue.length) {
      const currentId = queue.shift();
      if (typeof currentId !== 'number') {
        continue;
      }
      const children = relationsIndexer.getEntityChildren(modelGroup, currentId);
      for (const childId of Array.from(children)) {
        if (collected.has(childId)) {
          continue;
        }
        collected.add(childId);
        queue.push(childId);
      }
    }

    return collected;
  }, []);

  const getEntitiesWithChildrenFragmentMap = useCallback((modelGroup: OBF.FragmentsGroup, expressIDs: Iterable<number>) => {
    const ids = new Set<number>();
    for (const expressID of Array.from(expressIDs)) {
      const relatedIds = collectEntityAndDescendantIds(modelGroup, expressID);
      for (const relatedId of Array.from(relatedIds)) {
        ids.add(relatedId);
      }
    }

    return modelGroup.getFragmentMap(ids);
  }, [collectEntityAndDescendantIds]);

  const buildEvacuationIndexes = useCallback(async (modelGroup: OBF.FragmentsGroup) => {
    const pathIds = new Set<number>();
    const allPathRenderIds = new Set<number>();
    const pathByGlobalId = new Map<string, Set<number>>();
    const pathByStepId = new Map<string, Set<number>>();
    const lightByGlobalId = new Map<string, number>();
    const lightByStepId = new Map<string, number>();

    const appendPathIndex = (index: Map<string, Set<number>>, key: string | null, expressID: number) => {
      if (!key) {
        return;
      }
      if (!index.has(key)) {
        index.set(key, new Set());
      }
      index.get(key)?.add(expressID);
    };

    const propertySets = await modelGroup.getAllPropertiesOfType(WEBIFC.IFCPROPERTYSET);
    for (const propertySet of Object.values(propertySets ?? {})) {
      const pset = propertySet as IfcEntityProperties;
      const psetId = pset?.expressID;
      const psetName = getStringValue(pset?.Name);
      if (typeof psetId !== 'number' || !psetName) {
        continue;
      }
      if (![EVACUATION_PATH_PSET, EVACUATION_LIGHT_PSET].includes(psetName)) {
        continue;
      }

      const relationId = await OBC.IfcPropertiesUtils.getPsetRel(modelGroup, psetId);
      const relation = relationId ? await modelGroup.getProperties(relationId) as IfcEntityProperties | null : null;
      const relationTargets = relation?.RelatedObjects;
      const relatedObjects = Array.isArray(relationTargets)
        ? relationTargets.map((item) => Number(item?.value)).filter((item) => Number.isFinite(item))
        : [];

      const values = await getPropertyValuesFromPsetId(modelGroup, psetId);
      for (const relatedId of relatedObjects) {
        const entity = await modelGroup.getProperties(relatedId) as IfcEntityProperties | null;
        const entityGlobalId = getStringValue(entity?.GlobalId);

        if (psetName === EVACUATION_PATH_PSET) {
          pathIds.add(relatedId);
          const pathRenderIds = collectEntityAndDescendantIds(modelGroup, relatedId);
          for (const childId of Array.from(pathRenderIds)) {
            allPathRenderIds.add(childId);
          }
          if (entityGlobalId) {
            appendPathIndex(pathByGlobalId, entityGlobalId, relatedId);
          }
          const pathGlobalId = getStringValue(values?.PathGlobalId);
          const pathStepId = getStringValue(values?.PathStepId);
          appendPathIndex(pathByGlobalId, pathGlobalId, relatedId);
          appendPathIndex(pathByStepId, String(relatedId), relatedId);
          appendPathIndex(pathByStepId, pathStepId, relatedId);
        }

        if (psetName === EVACUATION_LIGHT_PSET) {
          if (entityGlobalId) {
            lightByGlobalId.set(entityGlobalId, relatedId);
          }
          lightByStepId.set(String(relatedId), relatedId);
          const lightEI = getStringValue(values?.LightEI);
          if (lightEI) {
            lightByStepId.set(lightEI, relatedId);
          }
        }
      }
    }

    evacuationPathIdsRef.current = pathIds;
    evacuationPathFragmentMapRef.current = allPathRenderIds.size ? modelGroup.getFragmentMap(allPathRenderIds) : null;
    evacuationPathByGlobalIdRef.current = pathByGlobalId;
    evacuationPathByStepIdRef.current = pathByStepId;
    evacuationLightByGlobalIdRef.current = lightByGlobalId;
    evacuationLightByStepIdRef.current = lightByStepId;

  }, [collectEntityAndDescendantIds, getPropertyValuesFromPsetId, getStringValue]);

  const highlightDoorEvacuation = useCallback(async (modelGroup: OBF.FragmentsGroup, doorExpressID: number) => {
    clearEvacuationHighlight();

    const entity = await modelGroup.getProperties(doorExpressID) as IfcEntityProperties | null;
    if (!entity || entity.type !== WEBIFC.IFCDOOR) {
      return;
    }

    const binding = await getDoorEvacuationBinding(modelGroup, doorExpressID);
    if (!binding || !binding.hasEvacuationPath || (!binding.primaryEvacuationPathGlobalId && !binding.primaryEvacuationPathStepId)) {
      message.info('该门没有绑定疏散路径');
      return;
    }

    let pathExpressIDs = binding.primaryEvacuationPathGlobalId
      ? findPathEntitiesByGlobalId(binding.primaryEvacuationPathGlobalId)
      : [];

    if (!pathExpressIDs.length && binding.primaryEvacuationPathGlobalId) {
      const directPathId = await findEntityByGlobalId(modelGroup, binding.primaryEvacuationPathGlobalId);
      if (directPathId != null) {
        pathExpressIDs = [directPathId];
      }
    }

    if (!pathExpressIDs.length && binding.primaryEvacuationPathStepId) {
      pathExpressIDs = findPathEntitiesByStepId(binding.primaryEvacuationPathStepId);
    }

    if (!pathExpressIDs.length && binding.primaryEvacuationPathStepId) {
      const directPathId = await findEntityByStepId(modelGroup, binding.primaryEvacuationPathStepId);
      if (directPathId != null) {
        pathExpressIDs = [directPathId];
      }
    }

    if (!pathExpressIDs.length) {
      message.info('该门没有绑定疏散路径');
      return;
    }

    const pathBindings = (await Promise.all(
      pathExpressIDs.map((pathExpressID) => getEvacuationPathBinding(modelGroup, pathExpressID)),
    )).filter((item): item is EvacuationPathBinding => Boolean(item));

    const mergedPathBinding: EvacuationPathBinding | null = pathBindings.length ? {
      pathGlobalId: pathBindings[0].pathGlobalId,
      pathStepId: pathBindings[0].pathStepId,
      strategy: pathBindings[0].strategy,
      doorEI: pathBindings[0].doorEI,
      doorGlobalId: pathBindings[0].doorGlobalId,
      doorStepId: pathBindings[0].doorStepId,
      relatedLightGlobalIds: Array.from(new Set(pathBindings.flatMap((item) => item.relatedLightGlobalIds))),
      relatedLightStepIds: Array.from(new Set(pathBindings.flatMap((item) => item.relatedLightStepIds))),
    } : null;

    const pathMap = getEntitiesWithChildrenFragmentMap(modelGroup, pathExpressIDs);
    currentEvacuationPathMapRef.current = pathMap;
    syncEvacuationPathVisibility();

    const highlighter = highlighterRef.current;
    if (highlighter) {
      await highlighter.highlightByID('evacuationPath', pathMap, true, false);
    }

    const relatedLightIds = new Set<number>(await findEntitiesByGlobalIds(modelGroup, mergedPathBinding?.relatedLightGlobalIds ?? []));
    const resolvedGlobalIds = new Set<string>();
    for (const globalId of mergedPathBinding?.relatedLightGlobalIds ?? []) {
      const lightEntityId = await findEntityByGlobalId(modelGroup, globalId, evacuationLightByGlobalIdRef.current);
      if (lightEntityId != null) {
        relatedLightIds.add(lightEntityId);
        resolvedGlobalIds.add(globalId);
      }
    }

    const fallbackStepIds: string[] = [];
    const pathStepIds = mergedPathBinding?.relatedLightStepIds ?? [];
    if (mergedPathBinding?.relatedLightGlobalIds?.length) {
      mergedPathBinding.relatedLightGlobalIds.forEach((globalId, index) => {
        if (!resolvedGlobalIds.has(globalId) && pathStepIds[index]) {
          fallbackStepIds.push(pathStepIds[index]);
        }
      });
      if (pathStepIds.length > mergedPathBinding.relatedLightGlobalIds.length) {
        fallbackStepIds.push(...pathStepIds.slice(mergedPathBinding.relatedLightGlobalIds.length));
      }
    } else {
      fallbackStepIds.push(...pathStepIds);
    }

    for (const stepId of fallbackStepIds) {
      const lightEntityId = await findEntityByStepId(modelGroup, stepId, evacuationLightByStepIdRef.current);
      if (lightEntityId != null) {
        relatedLightIds.add(lightEntityId);
      }
    }

    const relatedLightIdList = Array.from(relatedLightIds);
    if (relatedLightIdList.length && highlighter) {
      const lightRenderIds = new Set<number>();
      for (const lightId of relatedLightIdList) {
        const relatedIds = collectEntityAndDescendantIds(modelGroup, lightId);
        for (const childId of Array.from(relatedIds)) {
          lightRenderIds.add(childId);
        }
      }
      const lightMap = modelGroup.getFragmentMap(lightRenderIds);
      currentEvacuationLightMapRef.current = lightMap;
      await highlighter.highlightByID('evacuationLights', lightMap, true, false);
    }
  }, [
    clearEvacuationHighlight,
    collectEntityAndDescendantIds,
    findEntitiesByGlobalIds,
    findEntityByGlobalId,
    findEntityByStepId,
    findPathEntitiesByGlobalId,
    findPathEntitiesByStepId,
    getDoorEvacuationBinding,
    getEntitiesWithChildrenFragmentMap,
    getEvacuationPathBinding,
    syncEvacuationPathVisibility,
  ]);

  const focusStorey = useCallback((fragmentIdMap: OBF.FragmentIdMap | null) => {
    if (!fragmentIdMap || !bboxer || !world?.camera?.controls) {
      return;
    }

    try {
      bboxer.addFragmentIdMap(fragmentIdMap);
      const mesh = bboxer.getMesh();
      const { x, y, z } = mesh.position;
      bboxer.reset();
      world.camera.controls.setLookAt(x, y + 20, z, x, y - 20, z, true);
    } catch (error) {
      bboxer.reset();
    }
  }, [bboxer, world]);

  const applyVisibilityFilters = useCallback(() => {
    if (!hider || !classifier) {
      return;
    }

    hider.set(false);

    if (selectedStoreyKey === 'all') {
      hider.set(true);
    } else {
      const currentStorey = storeys.find((item) => item.key === selectedStoreyKey);
      const storeyMap = currentStorey ? getStoreyFragmentMap(currentStorey) : null;
      if (storeyMap) {
        hider.set(true, storeyMap);
      } else {
        hider.set(true);
      }
    }

    ENTITY_TOGGLE_CONFIGS.forEach((config) => {
      if (entityVisibility[config.key]) {
        return;
      }
      const entityMap = classifier.find({ entities: config.entities });
      hider.set(false, entityMap);
    });
    syncEvacuationPathVisibility();
  }, [classifier, entityVisibility, getStoreyFragmentMap, hider, selectedStoreyKey, storeys, syncEvacuationPathVisibility]);

  useEffect(() => {
    let cancelled = false;
    let localComponents: OBC.Components | null = null;

    setLoaded(false);
    setHasIfcFile(true);
    setEmptyStateMessage('暂无可预览的IFC模型');
    setWorld(null);
    setModel(null);
    setClassifier(null);
    setHider(null);
    setIndexer(null);
    setBboxer(null);
    setStoreys([]);
    setSelectedStoreyKey('all');
    setEntityVisibility(DEFAULT_ENTITY_VISIBILITY);
    hiderRef.current = null;
    indexerRef.current = null;
    highlighterRef.current = null;
    propertySetCacheRef.current.clear();
    evacuationPathIdsRef.current = new Set();
    evacuationPathFragmentMapRef.current = null;
    evacuationPathByGlobalIdRef.current = new Map();
    evacuationPathByStepIdRef.current = new Map();
    evacuationLightByGlobalIdRef.current = new Map();
    evacuationLightByStepIdRef.current = new Map();
    currentEvacuationPathMapRef.current = null;
    currentEvacuationLightMapRef.current = null;

    if (!activeVersionCanUseFile || !activeVersionProfile) {
      setHasIfcFile(false);
      setEmptyStateMessage(activeVersionEmptyMessage);
      setLoaded(true);
      return () => {
        cancelled = true;
        clearEvacuationHighlight();
      };
    }
    const versionProfile = activeVersionProfile;
    const versionDownloadUrl = activeVersionDownloadUrl;

    const initIFCViewer = async () => {
      if (!ifcRef.current) return;

      const components = new OBC.Components();
      localComponents = components;
      const worlds = components.get(OBC.Worlds);
      const nextWorld = worlds.create<
        OBC.SimpleScene,
        OBC.OrthoPerspectiveCamera,
        // @ts-ignore
        OBCF.PostproductionRenderer
      >();
      nextWorld.scene = new OBC.SimpleScene(components);
      // @ts-ignore
      nextWorld.renderer = new OBCF.PostproductionRenderer(components, ifcRef.current, {
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
      nextWorld.camera = new OBC.OrthoPerspectiveCamera(components);
      components.init();
      nextWorld.renderer.postproduction.enabled = true;
      nextWorld.scene.setup();
      nextWorld.camera.controls.setLookAt(12, 6, 8, 0, 0, -10);

      const grids = components.get(OBC.Grids);
      const grid = grids.create(nextWorld);
      nextWorld.renderer.postproduction.customEffects.excludedMeshes.push(grid.three);

      const fragmentIfcLoader = components.get(OBC.IfcLoader);
      await fragmentIfcLoader.setup();
      fragmentIfcLoader.settings.wasm = {
        path: 'https://unpkg.com/web-ifc@0.0.68/',
        absolute: true,
      };
      const excludedCats = [
        WEBIFC.IFCTENDONANCHOR,
        WEBIFC.IFCREINFORCINGBAR,
        WEBIFC.IFCREINFORCINGELEMENT,
      ];
      for (const cat of excludedCats) {
        fragmentIfcLoader.settings.excludedCategories.add(cat);
      }
      fragmentIfcLoader.settings.webIfc.COORDINATE_TO_ORIGIN = true;

      message.info(`加载${IFC_PROFILE_LABELS[versionProfile]}模型文件中`);
      let loadedModel: OBF.FragmentsGroup;
      try {
        const res = versionDownloadUrl
          ? await TuzhiVisService.getIFCByDownloadUrl(versionDownloadUrl)
          : await TuzhiVisService.getIFCBySpaceId(spaceId, versionProfile);
        const ifcFile = new File([res.data], 'tuzhi.ifc');
        const data = await ifcFile.arrayBuffer();
        const buffer = new Uint8Array(data);
        if (!buffer.length) {
          throw new Error('获取到的IFC文件内容为空');
        }
        loadedModel = await fragmentIfcLoader.load(buffer);
        loadedModel.name = 'tuzhi-ifc';
      } catch (error) {
        if (!cancelled) {
          setHasIfcFile(false);
          setEmptyStateMessage(`${IFC_PROFILE_LABELS[versionProfile]}模型解析失败`);
          message.error('IFC文件解析失败');
          console.error('IFC load failed:', error);
          setLoaded(true);
        }
        return;
      }

      try {
        nextWorld.scene.three.add(loadedModel);

        const nextBboxer = components.get(OBC.BoundingBoxer);
        nextBboxer.add(loadedModel);
        const mesh = nextBboxer.getMesh();
        const center = mesh.position;
        nextBboxer.reset();
        nextWorld.camera.controls.rotatePolarTo(Math.PI / 2, true);
        nextWorld.camera.controls.setLookAt(center.x, center.y + 20, center.z, center.x, center.y - 40, center.z, true);

        const nextIndexer = components.get(OBC.IfcRelationsIndexer);
        await nextIndexer.process(loadedModel);

        const nextClassifier = components.get(OBC.Classifier);
        nextClassifier.byEntity(loadedModel);
        await nextClassifier.bySpatialStructure(loadedModel, {
          isolate: new Set([WEBIFC.IFCBUILDINGSTOREY]),
        });

        // @ts-ignore
        const highlighter = components.get(OBCF.Highlighter);
        // @ts-ignore
        highlighter.setup({ world: nextWorld });
        // @ts-ignore
        highlighter.zoomToSelection = false;
        // @ts-ignore
        highlighter.add('evacuationPath', new THREE.Color('#f97316'));
        // @ts-ignore
        highlighter.add('evacuationLights', new THREE.Color('#06b6d4'));

        const nextStoreys = Object.values(nextClassifier.list.spatialStructures)
          .map((item: any) => {
            if (!item || item.id == null || !item.name) {
              return null;
            }
            return {
              id: item.id,
              key: String(item.id),
              name: item.name,
            };
          })
          .filter((item): item is StoreyOption => item !== null);

        await buildEvacuationIndexes(loadedModel);
        const selectEventName = highlighter.config.selectName;
        highlighter.events[selectEventName].onHighlight.add(async (selection) => {
          if (cancelled) {
            return;
          }
          const expressID = getExpressIdFromSelection(selection);
          if (expressID == null) {
            clearEvacuationHighlight();
            return;
          }
          await highlightDoorEvacuation(loadedModel, expressID);
        });
        highlighter.events[selectEventName].onClear.add(() => {
          if (cancelled) {
            return;
          }
          clearEvacuationHighlight();
        });

        if (cancelled) {
          return;
        }

        setWorld(nextWorld);
        setModel(loadedModel);
        setClassifier(nextClassifier);
        const nextHider = components.get(OBC.Hider);
        setHider(nextHider);
        setIndexer(nextIndexer);
        setBboxer(nextBboxer);
        setStoreys(nextStoreys);
        setHasIfcFile(true);
        hiderRef.current = nextHider;
        indexerRef.current = nextIndexer;
        highlighterRef.current = highlighter;
        syncEvacuationPathVisibility();
      } catch (error) {
        if (!cancelled) {
          setHasIfcFile(false);
          setEmptyStateMessage(`${IFC_PROFILE_LABELS[versionProfile]}模型初始化失败`);
          message.error('IFC模型初始化失败');
          console.error('IFC post-load init failed:', error);
        }
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    };

    initIFCViewer();

    return () => {
      cancelled = true;
      clearEvacuationHighlight();
      hiderRef.current = null;
      indexerRef.current = null;
      highlighterRef.current = null;
      try {
        // @ts-ignore
        localComponents?.dispose?.();
      } catch (error) {
        // ignore viewer teardown errors
      }
    };
  }, [
    activeVersionCanUseFile,
    activeVersionDownloadUrl,
    activeVersionEmptyMessage,
    activeVersionProfile,
    buildEvacuationIndexes,
    clearEvacuationHighlight,
    getExpressIdFromSelection,
    highlightDoorEvacuation,
    spaceId,
    syncEvacuationPathVisibility,
  ]);

  useEffect(() => {
    applyVisibilityFilters();
  }, [applyVisibilityFilters]);

  useEffect(() => {
    if (selectedStoreyKey === 'all') {
      return;
    }
    const currentStorey = storeys.find((item) => item.key === selectedStoreyKey);
    if (!currentStorey) {
      return;
    }
    focusStorey(getStoreyFragmentMap(currentStorey));
  }, [focusStorey, getStoreyFragmentMap, selectedStoreyKey, storeys]);

  useEffect(() => {
    if (!world?.renderer || !width || !height) {
      return;
    }
    try {
      world.renderer.resize(new THREE.Vector2(width, height));
      if (world.camera?.updateAspect) {
        world.camera.updateAspect();
      }
    } catch (error) {
      // ignore the first resize ticks before renderer is ready
    }
  }, [width, height]);

  return (
    <div className={styles.root} ref={resizeRef}>
      {(!loaded || versionsLoading) && <Loading message={versionsLoading ? 'IFC版本列表加载中' : 'Loading'} />}
      <div
        ref={ifcRef}
        className={styles.ifcViewer}
        style={{
          width,
          height,
        }}
      />
      {loaded && !hasIfcFile && (
        <div className={styles.emptyState}>
          <Typography.Text strong>{emptyStateMessage}</Typography.Text>
          {activeVersion && (
            <Typography.Text type="secondary">
              当前版本：{IFC_PROFILE_LABELS[activeVersion.profile]}，状态：{IFC_STATUS_LABELS[activeVersion.status]}
            </Typography.Text>
          )}
        </div>
      )}
      {loaded && !versionsLoading && (
        <div className={styles.controls}>
          <div className={styles.controlsTitle}>IFC 模型</div>
          <div className={`${styles.controlBlock} ${styles.versionBlock}`}>
            <Typography.Text strong>版本列表</Typography.Text>
            <div className={styles.profileList}>
              {ifcVersions.length ? ifcVersions.map((version) => {
                const canPreview = canUseIfcVersionFile(version);
                const isSelected = version.profile === selectedProfile;
                const isExpanded = version.profile === expandedProfile;
                const isBusy = actionProfile === version.profile;
                return (
                  <div
                    key={version.profile}
                    className={[
                      styles.profileCard,
                      isSelected ? styles.profileCardActive : '',
                      isExpanded ? styles.profileCardExpanded : styles.profileCardCollapsed,
                    ].filter(Boolean).join(' ')}
                    onClick={() => setExpandedProfile(version.profile)}
                  >
                    <div className={styles.profileCardHeader}>
                      <Typography.Text strong>{IFC_PROFILE_LABELS[version.profile]}</Typography.Text>
                      <Tag color={IFC_STATUS_TAG_COLORS[version.status]}>{IFC_STATUS_LABELS[version.status]}</Tag>
                    </div>
                    {isExpanded && (
                      <div className={styles.profileCardDetail}>
                        <Typography.Text type="secondary" className={styles.profileDescription}>
                          {IFC_PROFILE_DESCRIPTIONS[version.profile]}
                        </Typography.Text>
                        <Typography.Text type="secondary" className={styles.profileMeta}>
                          {getIfcDurationDisplay(version)}
                        </Typography.Text>
                        {version.isLongRunning && (
                          <Typography.Text type="warning" className={styles.profileMeta}>
                            预计耗时较长，生成前请确认
                          </Typography.Text>
                        )}
                        {version.createTime && (
                          <Typography.Text type="secondary" className={styles.profileMeta}>
                            生成时间：{version.createTime}
                          </Typography.Text>
                        )}
                        <div className={styles.profileActions}>
                          <Button
                            size="small"
                            type={isSelected && canPreview ? 'primary' : 'default'}
                            disabled={!canPreview}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelectProfile(version.profile);
                            }}
                          >
                            查看
                          </Button>
                          {canPreview && (
                            <Button
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDownloadProfile(version.profile);
                              }}
                            >
                              下载
                            </Button>
                          )}
                          <Button
                            size="small"
                            loading={isBusy}
                            disabled={!canPreview}
                            onClick={(event) => {
                              event.stopPropagation();
                              onGenerateProfile(version.profile, { regenerate: true });
                            }}
                          >
                            重新生成
                          </Button>
                          {version.status === 'missing' && (
                            <Button
                              size="small"
                              loading={isBusy}
                              onClick={(event) => {
                                event.stopPropagation();
                                onGenerateProfile(version.profile);
                              }}
                            >
                              生成
                            </Button>
                          )}
                          {version.status === 'generating' && (
                            <Button size="small" disabled>
                              生成中
                            </Button>
                          )}
                          {version.status === 'failed' && (
                            <Button
                              size="small"
                              danger
                              loading={isBusy}
                              onClick={(event) => {
                                event.stopPropagation();
                                onGenerateProfile(version.profile);
                              }}
                            >
                              重试
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }) : (
                <Typography.Text type="secondary">暂无IFC版本信息</Typography.Text>
              )}
            </div>
          </div>
          {hasIfcFile && (
            <>
          <div className={styles.controlBlock}>
            <Typography.Text strong>楼层</Typography.Text>
            <Select
              className={styles.storeySelect}
              value={selectedStoreyKey}
              options={storeySelectOptions}
              onChange={(value) => setSelectedStoreyKey(value)}
            />
          </div>
          <div className={`${styles.controlBlock} ${styles.entityBlock}`}>
            <Typography.Text strong>构件显示</Typography.Text>
            <div className={styles.checkboxGroup}>
              {ENTITY_TOGGLE_CONFIGS.map((config) => (
                <Checkbox
                  className={styles.checkboxItem}
                  key={config.key}
                  checked={entityVisibility[config.key]}
                  onChange={(event) => {
                    setEntityVisibility((prev) => ({
                      ...prev,
                      [config.key]: event.target.checked,
                    }));
                  }}
                >
                  {config.label}
                </Checkbox>
              ))}
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default IFCViewer;